import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  deepPurpleMui,
  greyMui,
  orangeMui,
  redMui,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

export const BookingCalendarStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "booking-calendar",
})`
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

export const BookingCalendarWrapperStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "booking-calendar-wrapper",
  },
)<{
  $passengersCount: number;
  $daysCount: number;
}>`
  width: 100%;
  height: 100%;
  overflow: hidden;

  .head_dates_line {
    position: sticky;
    height: 40px;
    top: 0;
    z-index: 20;
    grid-row: fixedrow;
    grid-column-start: daycol 1;
    grid-column-end: daycol ${({ $daysCount }) => $daysCount + 1};
    display: flex;
    .renderIfVisible {
      width: 230px;
    }
  }

  .fixed_pass_column {
    width: 250px;
    position: sticky;
    inset-inline-start: 0;
    z-index: 20;
    grid-row-start: passrow 1;
    grid-row-end: passrow ${({ $passengersCount }) => $passengersCount + 1};
    grid-column: fixedcol;
  }

  .date-col-wrapper {
    width: 230px;
    grid-row-start: passrow 1;
    grid-row-end: passrow ${({ $passengersCount }) => $passengersCount + 1};
  }

  .booking_cell {
    background-color: ${rydeColors.white};
    border-inline-end: thin solid transparent;
    border-bottom: thin solid transparent;
    box-shadow:
      inset -1px 0 0 ${greyMui._300},
      inset 0 -1px 0 ${greyMui._300};
    padding-inline-start: 5px;
    padding-top: 5px;
    font-size: 13px;
    overflow-y: auto;
    position: relative;
    z-index: ${ScreenDepth.MAIN_CONTAINER};
    height: 100px;
    width: 230px;

    &:hover {
      outline: 3px solid ${deepPurpleMui.A100};
      outline-offset: -3px;

      .calendar_cell-footer {
        display: block;
      }
    }
    &.cell_selected {
      border: 1px solid ${deepPurpleMui.A100};
      background-color: ${rydeColors.selectedBookingCalendarBackground};
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.25);
    }

    .calendar_cell-dto_list {
      min-height: 100%;

      .BlockHighlight {
        margin-inline-end: 4px;
      }
    }

    .calendar_cell-footer {
      display: none;
      position: sticky;
      bottom: 2px;
      height: 0;
      padding-inline: 3px;
    }
    .calendar_cell-cta_wrapper {
      padding: 2px;
      border: 1px solid ${greyMui._300};
      position: absolute;
      bottom: 0;
      width: 100%;
      margin-inline-start: -5px;
      background-color: ${rydeColors.white};
    }

    .select_button {
      font-size: 12px;
      margin-inline-start: 8px;
      text-decoration: underline;
      color: ${redMui.A700};
      cursor: pointer;
      border: none;
      background-color: transparent;
    }
    .add_booking_button {
      margin-inline-start: 12px;
      font-size: 12px;
      text-decoration: underline;
      cursor: pointer;
      border: none;
      background-color: transparent;
    }
  }
  .booking_cell--in_past {
    background-color: ${rydeColors.bookingPastCalendarCells};
  }
  .cell_pass_fixed {
    -webkit-font-smoothing: subpixel-antialiased;
    display: flex;
    align-items: center;
    padding-inline-start: 16px;
    background-color: ${rydeColors.white};
    border-inline-start: thin solid transparent;
    border-inline-end: thin solid transparent;
    border-bottom: thin solid transparent;
    /*! @noflip */
    box-shadow:
      inset 1px 0 0 ${greyMui._300},
      inset -1px 0 0 ${greyMui._300},
      inset 0 -1px 0 ${greyMui._300};

    will-change: transform;
    transform: scale(1);
    .checkbox {
      display: flex;
      align-items: center;
    }
    .passenger_info {
      display: flex;
      width: 155px;
      flex-direction: column;
      align-items: stretch;
      justify-content: center;
      gap: 2px;

      .passenger_info_first_row {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .mobile,
      .unit,
      .code,
      .area {
        font-size: 12px;
        color: ${greyMui._700};
      }
    }
  }
  .cell_row {
    height: 24px;
    display: flex;
    align-items: center;
    gap: 3px;
    padding-inline-end: 7px;
    .direction_cell {
      width: 48px;
      display: flex;
    }
    .time_cell {
      font-size: 12px;
      width: 54px;
    }
    .status_cell {
      font-size: 12px;
      flex: 1 1 auto;
      text-overflow: ellipsis;
    }
    .hub_cell {
      display: flex;
      align-items: center;
      gap: 2px;
      margin-inline-start: 3px;
      svg {
        width: 14px;
        height: 14px;
      }
      &__incomplete-icon {
        color: ${orangeMui._800};
      }
    }
    .meal_cell {
      display: flex;
      align-items: center;
      margin-inline-start: 3px;
      svg {
        width: 14px;
        height: 18px;
        path {
          fill: ${deepPurpleMui._200};
        }
      }
    }
    .actions_cell {
      display: none;
      align-items: center;
      justify-content: space-between;
      color: ${blueGreyMui.A400};
      width: 33px;
      margin-inline-start: auto;
      .edit-booking {
        font-size: medium;
        cursor: pointer;
        &:hover {
          color: ${blueMui._600};
        }
      }
      .delete-booking {
        font-size: medium;
        cursor: pointer;
        &:hover {
          color: ${blueMui._600};
        }
      }
      .deleteBookingDisabled {
        color: ${greyMui._500};
        &:hover {
          color: ${greyMui._500};
        }
        cursor: not-allowed;
      }
    }
    &:hover .actions_cell {
      display: flex;
    }
  }
  .cell_row__highlight {
    background-color: ${rydeColors.rowHighlight};

    &:hover {
      background-color: ${deepPurpleMui._50};
    }
  }
  .head_cell {
    background-color: ${rydeColors.gridCellHover};
    color: ${blueGreyMui.A400};
    display: flex;
    align-items: center;
    padding-inline-start: 10px;
    padding-inline-end: 10px;
    border-inline-end: thin solid transparent;
    border-bottom: thin solid transparent;
    box-shadow:
      inset -1px 0 0 ${greyMui._300},
      inset 0 -1px 0 ${greyMui._300};
    font-size: 11px;
    width: 230px;
    height: 40px;
    .checkbox {
      display: flex;
      align-items: center;
    }
    .checkbox_label {
      margin-inline-start: 5px;
    }
    .full_date {
      margin-inline-start: auto;
    }
  }
`;

export const BookingCalendarFirstCellStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "booking-calendar-first-cell",
  },
)`
  background-color: ${rydeColors.gridCellHover};
  display: flex;
  align-items: center;
  padding-inline-start: 16px;
  font-size: 11px;
  border-inline-start: thin solid transparent;
  border-inline-end: thin solid transparent;
  border-bottom: thin solid transparent;
  /*! @noflip */
  box-shadow:
    inset 1px 0 0 ${greyMui._300},
    inset -1px 0 0 ${greyMui._300},
    inset 0 -1px 0 ${greyMui._300};
  grid-column: fixedcol;
  grid-row: fixedrow;
  z-index: 25;
  .title {
    margin-inline-start: 2px;
  }
`;

export const NoCalendarBookingDataStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "no-calendar-booking-data",
})`
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

export const CalendarBookingTooltipContentStyled = styled.span.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "calendar-booking-tooltip-content",
  },
)``;

export const CalendarBookingRideIdLinkStyled = styled.span.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "calendar-booking-ride-id-link",
  },
)`
  color: ${rydeColors.dragBorderColor};
  text-decoration: underline;

  .calendar-booking-ride-id-link-clickable {
    cursor: pointer;
  }
`;
